/**
 * Consent state. The default pixel reports truthfully and lets the collector
 * enforce policy; regulated sites may opt into clientHardGate, which blocks
 * queueing/sending until explicit analytics consent. Unknown keys are explicit
 * null + source 'unknown' (attr-security ruling).
 */
import type { ConsentState } from './types';
import { nowIso } from './util';

export function unknownConsent(): ConsentState {
  return {
    analytics: null,
    marketing: null,
    personalization: null,
    saleOrShare: null,
    source: 'unknown',
    updatedAt: nowIso(),
  };
}

export function mergeConsent(
  current: ConsentState,
  patch: Partial<ConsentState> | undefined,
  source: ConsentState['source']
): ConsentState {
  const next: ConsentState = { ...current };
  if (patch) {
    if ('collectionFields' in patch) next.collectionFields = patch.collectionFields;
    if ('receiptId' in patch) next.receiptId = patch.receiptId;
    if ('receiptProof' in patch) next.receiptProof = patch.receiptProof;
    if ('sitePersonalization' in patch) next.sitePersonalization = patch.sitePersonalization;
    if ('analytics' in patch) next.analytics = normBool(patch.analytics);
    if ('marketing' in patch) next.marketing = normBool(patch.marketing);
    if ('personalization' in patch) next.personalization = normBool(patch.personalization);
    if ('saleOrShare' in patch) next.saleOrShare = normBool(patch.saleOrShare);
  }
  next.source = (patch && patch.source) || source;
  next.updatedAt = nowIso();
  if (typeof navigator !== 'undefined' && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) next.saleOrShare = true;
  return next;
}

function normBool(v: unknown): boolean | null {
  if (v === true || v === false) return v;
  if (v === null) return null;
  if (v === 'true' || v === 1) return true;
  if (v === 'false' || v === 0) return false;
  return null;
}

export function analyticsGranted(c: ConsentState): boolean {
  return c.analytics === true;
}

export function marketingGranted(c: ConsentState): boolean {
  return c.marketing === true && c.saleOrShare !== true;
}
